import { selectBuCode, selectCountryCode } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectDeliveryContact } from '@repo/api-adapters/selectors/customer.selectors';
import { type SelectedServices, selectRawProvidedServices } from '@repo/api-adapters/selectors/services.selectors';
import {
    buildCompositeServiceId,
    findDeliveryServiceByServiceId,
    getAllDeliveryTransportTimeWindows,
} from '@repo/api-adapters/services/services.api.utils';
import { buildTransportTimeWindow } from '@repo/api-adapters/services/time-windows.utils';
import type { AlignProposalPayload, AlignProposalTransportTimewindow } from '@repo/types/align-proposal';
import type { OrderAsset } from '@repo/types/order';
import { getRegion } from '@repo/utils/region.utils';

import type { AlignProposalQueryParams } from './align-proposal.api';

interface AlignProposalCandidateTimeWindow {
    timeWindowId: string;
    fromDateTimeUtc: string;
    toDateTimeUtc: string;
}

interface BuildAlignProposalPayloadParams {
    orderData: OrderAsset;
    services: SelectedServices;
    triggerServiceId: string;
    isDeliveryTrigger: boolean;
    candidateTimeWindow: AlignProposalCandidateTimeWindow;
}

export const buildAlignProposalQueryParams = (orderData: OrderAsset): AlignProposalQueryParams | undefined => {
    const country = selectCountryCode(orderData);
    const buCode = selectBuCode(orderData);
    const zipCode = selectDeliveryContact(orderData)?.zipCode;

    if (!country || !buCode || !zipCode) {
        return undefined;
    }

    return { country, buCode, zipCode };
};

const getAlignProposalTransportTimewindows = (
    orderData: OrderAsset,
    deliveryOverride: ReturnType<typeof buildTransportTimeWindow> | undefined
): AlignProposalTransportTimewindow[] => {
    return getAllDeliveryTransportTimeWindows(orderData, deliveryOverride).flatMap((transportTimewindow) => {
        if (!transportTimewindow.serviceIdentifier) {
            return [];
        }

        return [
            {
                ...transportTimewindow,
                serviceIdentifier: transportTimewindow.serviceIdentifier,
            },
        ];
    });
};

export const buildAlignProposalPayload = ({
    orderData,
    services,
    triggerServiceId,
    isDeliveryTrigger,
    candidateTimeWindow,
}: BuildAlignProposalPayloadParams): AlignProposalPayload | undefined => {
    const locality = getRegion(selectCountryCode(orderData));
    const selectedServicesById = new Map(services.map((service) => [service.serviceId, service]));
    const providedServices = selectRawProvidedServices(orderData);
    const triggerDeliveryNo = isDeliveryTrigger
        ? findDeliveryServiceByServiceId(orderData, triggerServiceId)?.deliveryNo
        : undefined;
    const deliveryOverride = triggerDeliveryNo
        ? buildTransportTimeWindow(triggerDeliveryNo, candidateTimeWindow)
        : undefined;
    const transportTimewindows = getAlignProposalTransportTimewindows(orderData, deliveryOverride);

    const serviceLines = providedServices.flatMap((providedService) => {
        const serviceId = buildCompositeServiceId(providedService.serviceItemType, providedService.serviceItemNo);

        if (!isDeliveryTrigger && serviceId !== triggerServiceId) {
            return [];
        }

        const currentTimeWindow = selectedServicesById.get(serviceId)?.timeWindow;
        const isTriggerService = !isDeliveryTrigger && serviceId === triggerServiceId;
        const timeWindowId = isTriggerService ? candidateTimeWindow.timeWindowId : currentTimeWindow?.timeWindowId;
        const fromDate = isTriggerService ? candidateTimeWindow.fromDateTimeUtc : currentTimeWindow?.fromDateTimeUtc;
        const manualChangeType: 'STARTTIME' | 'NO_CHANGE' = isTriggerService ? 'STARTTIME' : 'NO_CHANGE';

        if (
            !serviceId ||
            !providedService.serviceProductId ||
            !providedService.serviceProviderId ||
            !timeWindowId ||
            !fromDate
        ) {
            return [];
        }

        return [
            {
                capacityNeeded: providedService.capacityValue ?? 0,
                capacityUnit: providedService.capacityUnit ?? '',
                serviceNo: serviceId,
                serviceCode: providedService.serviceProductId,
                serviceProviderId: providedService.serviceProviderId,
                timeWindowId,
                manualChangeType,
                fromDate,
                transportTimewindows,
            },
        ];
    });

    if (serviceLines.length === 0) {
        return undefined;
    }

    return {
        locality,
        serviceLines,
    };
};
